import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, MenuItem, Typography, IconButton, Snackbar, Alert } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CropIcon from '@mui/icons-material/Crop';
import CloseIcon from '@mui/icons-material/Close';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import Loader from './Loader';
import ImageCropDialog from './ImageCropDialog';
import { color, font, bevel } from '../styles/tokens';

function BlogForm({ websites, user }) {
  const [title, setTitle] = useState('');
  const [website, setWebsite] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [cropFile, setCropFile] = useState(null);
  const [publishDate, setPublishDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [notice, setNotice] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!image) { setImagePreview(null); return; }
    const url = URL.createObjectURL(image);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  useEffect(() => {
    if (!expanded) return;
    document.body.style.overflow = 'hidden';
    const onKeyDown = e => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [expanded]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError('');

    const plainTextContent = content.replace(/<[^>]+>/g, '').trim();
    if (!title.trim() || !website || !plainTextContent) {
      setFieldError('Title, property and content are required.');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('title', title);
    formData.append('website', website);
    formData.append('content', content);
    if (image) formData.append('image', image);
    if (publishDate) formData.append('publishDate', publishDate);

    const token = localStorage.getItem('token');
    try {
      await axios.post('/api/blogs', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotice({ severity: 'success', message: 'Dispatch filed.' });
      setExpanded(false);
      setTitle('');
      setContent('');
      setImage(null);
      setCropFile(null);
      setPublishDate('');
      setWebsite('');
    } catch (err) {
      setNotice({ severity: 'error', message: err?.response?.data?.message || 'Couldn’t file this post. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  if (!websites.length) {
    return (
      <Alert severity="info" sx={{ maxWidth: 480 }}>
        You don’t have access to any property yet — ask a superadmin to assign you one in the Press Room.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 880 }}>
      {fieldError && <Alert severity="error" sx={{ mb: 2 }}>{fieldError}</Alert>}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px 1fr' }, gap: 4 }}
      >
        {/* Metadata rail */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            select
            label="Property"
            value={website}
            onChange={e => setWebsite(e.target.value)}
            required
            fullWidth
          >
            <MenuItem value="">Select property</MenuItem>
            {websites.map(w => (
              <MenuItem key={w._id} value={w._id}>{w.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Publish date"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={publishDate}
            onChange={e => setPublishDate(e.target.value)}
            fullWidth
            helperText="Leave blank to publish immediately"
          />
          <Box>
            <Typography sx={{ fontFamily: font.mono, fontSize: '.625rem', letterSpacing: '.1em', textTransform: 'uppercase', color: color.boneFaint, mb: 1 }}>
              Attached art
            </Typography>
            {imagePreview ? (
              <Box>
                <Box
                  component="img"
                  src={imagePreview}
                  alt=""
                  sx={{ width: '100%', display: 'block', border: `1px solid ${color.ruleSoft}`, mb: 1 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<CropIcon />} onClick={() => setCropFile(image)} sx={{ flex: 1 }}>
                    Edit crop
                  </Button>
                  <Button size="small" variant="outlined" color="error" onClick={() => setImage(null)}>
                    <CloseIcon fontSize="small" />
                  </Button>
                </Box>
              </Box>
            ) : (
              <Button variant="outlined" component="label" fullWidth>
                Upload image
                <input type="file" accept="image/*" hidden onChange={e => { if (e.target.files[0]) setCropFile(e.target.files[0]); e.target.value = ''; }} />
              </Button>
            )}
          </Box>
        </Box>

        {/* Headline + editor */}
        <Box
          sx={expanded ? {
            position: 'fixed', inset: 0, zIndex: 1300, background: color.ink,
            p: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          } : {}}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <TextField
              variant="standard"
              placeholder="Headline"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
              fullWidth
              InputProps={{
                disableUnderline: false,
                sx: {
                  fontFamily: font.display, fontWeight: 600, fontSize: { xs: '1.4rem', sm: '1.7rem' },
                  color: color.bone, mb: 3,
                },
              }}
            />
            <IconButton
              onClick={() => setExpanded(e => !e)}
              title={expanded ? 'Exit fullscreen' : 'Expand to fullscreen'}
              sx={{ mt: 1, color: color.boneDim, '&:hover': { color: color.cyan } }}
            >
              {expanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Box>
          <Box
            sx={{
              background: color.panel,
              border: `1px solid ${color.ruleSoft}`,
              clipPath: bevel(12),
              overflow: 'hidden',
              mb: 3,
              display: 'flex', flexDirection: 'column',
              flex: expanded ? 1 : 'none',
              minHeight: 0,
              '& .ql-toolbar': { borderRadius: 0, border: 'none', borderBottom: `1px solid ${color.ruleSoft}`, background: color.panel2, flex: 'none' },
              '& .ql-container': expanded
                ? { flex: 1, overflow: 'auto', background: '#fff', color: '#1d1e1f', border: 'none' }
                : { minHeight: 220, maxHeight: 520, resize: 'vertical', overflow: 'auto', background: '#fff', color: '#1d1e1f', border: 'none' },
              '& .ql-editor': { fontSize: 16 },
            }}
          >
            <ReactQuill value={content} onChange={setContent} placeholder="Write the dispatch…" />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flex: 'none' }}>
            <Button type="submit" variant="contained" color="primary">
              Publish dispatch
            </Button>
            {expanded && (
              <Button variant="outlined" onClick={() => setExpanded(false)}>
                Collapse
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={!!notice}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notice ? <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.message}</Alert> : undefined}
      </Snackbar>
      <ImageCropDialog
        open={!!cropFile}
        source={cropFile}
        onCancel={() => setCropFile(null)}
        onApply={croppedFile => { setImage(croppedFile); setCropFile(null); }}
        onError={message => setNotice({ severity: 'error', message })}
      />
    </Box>
  );
}

export default BlogForm;
